import { useState } from 'react'
import { Area, AreaChart, CartesianGrid, LabelList, XAxis } from "recharts"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/shared/components/ui/chart'
import { CalendarDays, Info } from 'lucide-react'

interface ActividadDiariaChartProps {
  data: Array<{ fecha: string; canjes: number; solicitudes: number }>
}

const chartConfig = {
  canjes: {
    label: "Canjes",
    color: "#14b8a6",
  },
} satisfies ChartConfig

export function ActividadDiariaChart({ data }: ActividadDiariaChartProps) {
  const [showInfo, setShowInfo] = useState(false)

  const chartData = data.map((item) => ({
    fecha: item.fecha,
    canjes: item.canjes,
  }))

  return (
    <Card className="border border-gray-300 dark:border-gray-700">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center">
          <CardTitle>Descargas por Día</CardTitle>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="text-muted-foreground hover:text-foreground transition-colors ml-2"
          >
            <Info className="h-4 w-4" />
          </button>
          {showInfo && (
            <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
              Descargas en los últimos 30 días
            </span>
          )}
        </div>
        <CalendarDays className="h-4 w-4 text-teal-500" />
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[250px] w-full">
          <AreaChart
            accessibilityLayer
            data={chartData}
            margin={{
              left: 12,
              right: 12,
              top: 20,
            }}
          >
            <defs>
              <linearGradient id="fillCanjes" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-canjes)" stopOpacity={0.4} />
                <stop offset="95%" stopColor="var(--color-canjes)" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="fecha"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) => {
                const date = new Date(value)
                return date.toLocaleDateString('es-EC', { day: '2-digit', month: 'short' })
              }}
            />
            <ChartTooltip
              cursor={{ stroke: '#14b8a6', strokeWidth: 1, strokeDasharray: '4 4' }}
              content={<ChartTooltipContent indicator="line" />}
            />
            <Area
              dataKey="canjes"
              type="natural"
              fill="url(#fillCanjes)"
              fillOpacity={1}
              stroke="var(--color-canjes)"
              strokeWidth={2}
              dot={({ cx, cy }: { cx: number; cy: number }) => (
                <g>
                  <circle cx={cx} cy={cy} r={5} fill="#fff" />
                  <circle cx={cx} cy={cy} r={3} fill="var(--color-canjes)" />
                </g>
              )}
              activeDot={({ cx, cy }: { cx: number; cy: number }) => (
                <g>
                  <circle cx={cx} cy={cy} r={6} fill="#fff" />
                  <circle cx={cx} cy={cy} r={4} fill="var(--color-canjes)" />
                </g>
              )}
            >
              <LabelList
                dataKey="canjes"
                position="top"
                offset={10}
                className="fill-foreground"
                fontSize={11}
              />
            </Area>
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
